import React, { useState, useEffect, useRef } from 'react';
import aiAutomationApi from '../utils/aiAutomationApi';

const JobPostingConfirmationModal = ({ isOpen, onClose, onProceed, collectedDetails, isDark }) => {
  const [isEditing, setIsEditing] = useState(true); // Start in edit mode by default
  const [formData, setFormData] = useState({
    company_name: '',
    employment_type: '',
    workplace_type: '',
    location: '',
    salary_min: '',
    salary_max: '',
    experience_min: '',
    experience_max: '',
    allow_call: true,
    recruiter_name: '',
    recruiter_number: '',
    department: '' // Added department field
  });
  const [errors, setErrors] = useState({});
  
  // Department dropdown state
  const [departments, setDepartments] = useState({});
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [departmentSearchQuery, setDepartmentSearchQuery] = useState('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const departmentDropdownRef = useRef(null);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setDepartmentLoading(true);
        const response = await aiAutomationApi.get('/naukri/departments');
        if (response?.success && response?.departments) {
          setDepartments(response.departments);
        }
      } catch (error) {
        console.warn('Failed to load departments:', error);
      } finally {
        setDepartmentLoading(false);
      }
    };
    
    if (isOpen) {
      fetchDepartments();
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setShowDepartmentDropdown(false);
      }
    };
    
    if (showDepartmentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDepartmentDropdown]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && collectedDetails) {
      const dept = collectedDetails.department || '';
      setFormData({
        company_name: collectedDetails.company_name || '',
        employment_type: collectedDetails.employment_type || '',
        workplace_type: collectedDetails.workplace_type || '',
        location: collectedDetails.location || '',
        salary_min: String(collectedDetails.salary_min || ''),
        salary_max: String(collectedDetails.salary_max || ''),
        experience_min: String(collectedDetails.experience_min ?? ''),
        experience_max: String(collectedDetails.experience_max ?? ''),
        allow_call: collectedDetails.allow_call !== false, // default true when undefined
        recruiter_name: collectedDetails.recruiter_name || '',
        recruiter_number: collectedDetails.recruiter_number || '',
        department: dept // Initialize department
      });
      // Start in edit mode directly - user can proceed when ready
      setIsEditing(true);
      setErrors({});
      setSelectedCategory('');
      setDepartmentSearchQuery('');
    }
  }, [isOpen, collectedDetails]);

  const validateForm = () => {
    const newErrors = {};
    
    // Validate company name
    if (!formData.company_name || !formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    } else if (/^\d+$/.test(formData.company_name.trim())) {
      newErrors.company_name = 'Company name cannot be only numbers';
    }
    
    // Validate department (MANDATORY)
    if (!formData.department || !formData.department.trim()) {
      newErrors.department = 'Department is required. Please select from the dropdown.';
    }
    
    // Validate salaries
    const minSalary = parseInt(formData.salary_min);
    const maxSalary = parseInt(formData.salary_max);
    
    if (!formData.salary_min || isNaN(minSalary) || minSalary <= 0) {
      newErrors.salary_min = 'Please enter a valid minimum salary';
    } else if (minSalary < 5000) {
      newErrors.salary_min = 'Minimum salary must be at least ₹5,000';
    }
    
    if (!formData.salary_max || isNaN(maxSalary) || maxSalary <= 0) {
      newErrors.salary_max = 'Please enter a valid maximum salary';
    }
    
    if (minSalary > 0 && maxSalary > 0 && maxSalary <= minSalary) {
      newErrors.salary_max = 'Maximum salary must be greater than minimum salary';
    }
    
    // Validate experience (required fields)
    const minExp = parseInt(formData.experience_min);
    const maxExp = parseInt(formData.experience_max);
    
    if (!formData.experience_min || formData.experience_min === '' || isNaN(minExp) || minExp < 0) {
      newErrors.experience_min = 'Please enter minimum experience in years (0 for fresher)';
    }
    
    if (!formData.experience_max || formData.experience_max === '' || isNaN(maxExp) || maxExp < 0) {
      newErrors.experience_max = 'Please enter maximum experience in years';
    }
    
    if (!isNaN(minExp) && !isNaN(maxExp) && maxExp <= minExp) {
      newErrors.experience_max = 'Maximum experience must be greater than minimum experience';
    }
    
    // Validate recruiter details if calls are allowed
    if (formData.allow_call) {
      if (!formData.recruiter_name || !formData.recruiter_name.trim()) {
        newErrors.recruiter_name = 'Recruiter name is required when calls are allowed';
      } else if (/^\d+$/.test(formData.recruiter_name.trim())) {
        newErrors.recruiter_name = 'Recruiter name cannot be only numbers';
      }
      
      const phoneDigits = formData.recruiter_number.replace(/\D/g, '');
      if (!phoneDigits || phoneDigits.length !== 10) {
        newErrors.recruiter_number = 'Phone number must be exactly 10 digits';
      } else if (!/^[6-9]/.test(phoneDigits)) {
        newErrors.recruiter_number = 'Phone number must start with 6, 7, 8, or 9';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveEdits = () => {
    if (validateForm()) {
      // Validation passed - proceed directly with job posting
      // Convert string values to proper types for backend
      const dataToSend = {
        ...formData,
        salary_min: parseInt(formData.salary_min) || 0,
        salary_max: parseInt(formData.salary_max) || 0,
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 0,
        _channels: collectedDetails?._channels || []
      };
      onProceed(dataToSend);
    }
  };

  const handleProceed = () => {
    // Always validate before proceeding
    if (!validateForm()) {
      return; // Stop if validation fails
    }
    
    if (!isEditing) {
      // Direct proceed from preview - include _channels from collectedDetails
      // Convert string values to proper types for backend
      const dataToSend = {
        ...formData,
        salary_min: parseInt(formData.salary_min) || 0,
        salary_max: parseInt(formData.salary_max) || 0,
        experience_min: parseInt(formData.experience_min) || 0,
        experience_max: parseInt(formData.experience_max) || 0,
        _channels: collectedDetails?._channels || []
      };
      onProceed(dataToSend);
    } else {
      // Go back to preview (validation already passed)
      setIsEditing(false);
    }
  };

  if (!isOpen) return null;

  const bgClass = isDark ? 'bg-gray-800' : 'bg-white';
  const textClass = isDark ? 'text-gray-100' : 'text-gray-900';
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-300';
  const inputBgClass = isDark ? 'bg-gray-700' : 'bg-gray-50';
  const inputTextClass = isDark ? 'text-gray-100' : 'text-gray-900';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`${bgClass} ${textClass} rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className={`p-6 border-b ${borderClass} flex items-center justify-between`}>
          <h2 className="text-2xl font-bold">
            {isEditing ? 'Edit Job Posting Details' : 'Confirm Job Posting Details'}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-gray-100 ${isDark ? 'hover:bg-gray-700' : ''} transition-colors`}
            title="Close and cancel job posting"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {!isEditing ? (
            // Preview Mode
            <div className="space-y-3">
              <div>
                <span className="font-semibold">Company Name:</span> {formData.company_name}
              </div>
              <div>
                <span className="font-semibold">Employment Type:</span> {formData.employment_type}
              </div>
              <div>
                <span className="font-semibold">Workplace Type:</span> {formData.workplace_type}
              </div>
              <div>
                <span className="font-semibold">Job Location:</span> {formData.location}
              </div>
              <div>
                <span className="font-semibold">Department:</span> {formData.department || <span className="text-red-500">Not selected (Required)</span>}
              </div>
              <div>
                <span className="font-semibold">Experience:</span> {formData.experience_min || 0} - {formData.experience_max || 0} years
              </div>
              <div>
                <span className="font-semibold">Minimum Salary:</span> ₹{parseInt(formData.salary_min || 0).toLocaleString()}
              </div>
              <div>
                <span className="font-semibold">Maximum Salary:</span> ₹{parseInt(formData.salary_max || 0).toLocaleString()}
              </div>
              <div>
                <span className="font-semibold">Allow Candidates to Call:</span> {formData.allow_call ? 'Yes' : 'No'}
              </div>
              {formData.allow_call && (
                <>
                  <div>
                    <span className="font-semibold">Recruiter Name:</span> {formData.recruiter_name}
                  </div>
                  <div>
                    <span className="font-semibold">Recruiter Number:</span> {formData.recruiter_number}
                  </div>
                </>
              )}
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Company Name *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.company_name && <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>}
              </div>
              
              {/* Department Nested Dropdown */}
              <div ref={departmentDropdownRef}>
                <label className="block text-sm font-medium mb-1">Department * (Select from dropdown)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.department}
                    onClick={() => setShowDepartmentDropdown(true)}
                    onChange={(e) => {
                      setDepartmentSearchQuery(e.target.value);
                      setShowDepartmentDropdown(true);
                    }}
                    placeholder={departmentLoading ? "Loading departments..." : "Click to select department"}
                    readOnly={departmentLoading}
                    className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${errors.department ? 'border-red-500' : ''}`}
                  />
                  
                  {showDepartmentDropdown && !departmentLoading && Object.keys(departments).length > 0 && (
                    <div className={`absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border ${borderClass} ${bgClass} rounded-md shadow-lg`}>
                      {/* Search input */}
                      <div className="sticky top-0 p-2 bg-inherit border-b">
                        <input
                          type="text"
                          value={departmentSearchQuery}
                          onChange={(e) => setDepartmentSearchQuery(e.target.value)}
                          placeholder="Search departments..."
                          className={`w-full px-2 py-1 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded text-sm focus:outline-none`}
                          autoFocus
                        />
                      </div>
                      
                      {/* Department categories and sub-departments */}
                      {Object.entries(departments)
                        .filter(([category]) => category !== 'All Departments')
                        .filter(([category, subDepts]) => {
                          if (!departmentSearchQuery) return true;
                          const searchLower = departmentSearchQuery.toLowerCase();
                          if (category.toLowerCase().includes(searchLower)) return true;
                          return Object.keys(subDepts || {}).some(sub => sub.toLowerCase().includes(searchLower));
                        })
                        .map(([category, subDepts]) => (
                          <div key={category}>
                            {/* Category header */}
                            <div
                              className={`px-3 py-2 font-semibold text-sm cursor-pointer hover:bg-blue-100 ${isDark ? 'hover:bg-gray-600' : ''} ${selectedCategory === category ? 'bg-blue-50' : ''}`}
                              onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                            >
                              <span className="mr-2">{selectedCategory === category ? '▼' : '▶'}</span>
                              {category}
                            </div>
                            
                            {/* Sub-departments (show when category is expanded or when searching) */}
                            {(selectedCategory === category || departmentSearchQuery) && Object.keys(subDepts || {}).length > 0 && (
                              <div className="pl-6">
                                {Object.keys(subDepts)
                                  .filter(sub => !departmentSearchQuery || sub.toLowerCase().includes(departmentSearchQuery.toLowerCase()))
                                  .map(subDept => (
                                    <div
                                      key={subDept}
                                      className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-200 ${isDark ? 'hover:bg-gray-500' : ''} ${formData.department === subDept ? 'bg-blue-100 font-medium' : ''}`}
                                      onClick={() => {
                                        handleInputChange('department', subDept);
                                        setShowDepartmentDropdown(false);
                                        setDepartmentSearchQuery('');
                                      }}
                                    >
                                      {subDept}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                {errors.department && <p className="text-red-500 text-sm mt-1">{errors.department}</p>}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Min Experience (years)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.experience_min}
                    onChange={(e) => handleInputChange('experience_min', e.target.value)}
                    className={`w-full px-3 py-2 border ${errors.experience_min ? 'border-red-500' : borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.experience_min && <p className="text-red-500 text-sm mt-1">{errors.experience_min}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Experience (years)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.experience_max}
                    onChange={(e) => handleInputChange('experience_max', e.target.value)}
                    className={`w-full px-3 py-2 border ${errors.experience_max ? 'border-red-500' : borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.experience_max && <p className="text-red-500 text-sm mt-1">{errors.experience_max}</p>}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Minimum Salary (₹) *</label>
                <input
                  type="number"
                  value={formData.salary_min}
                  onChange={(e) => handleInputChange('salary_min', e.target.value)}
                  className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.salary_min && <p className="text-red-500 text-sm mt-1">{errors.salary_min}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Maximum Salary (₹) *</label>
                <input
                  type="number"
                  value={formData.salary_max}
                  onChange={(e) => handleInputChange('salary_max', e.target.value)}
                  className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.salary_max && <p className="text-red-500 text-sm mt-1">{errors.salary_max}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Allow Candidates to Call *</label>
                <select
                  value={formData.allow_call ? 'yes' : 'no'}
                  onChange={(e) => handleInputChange('allow_call', e.target.value === 'yes')}
                  className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              
              {formData.allow_call && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Recruiter Name *</label>
                    <input
                      type="text"
                      value={formData.recruiter_name}
                      onChange={(e) => handleInputChange('recruiter_name', e.target.value)}
                      className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.recruiter_name && <p className="text-red-500 text-sm mt-1">{errors.recruiter_name}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Recruiter Phone Number (10 digits) *</label>
                    <input
                      type="tel"
                      value={formData.recruiter_number}
                      onChange={(e) => handleInputChange('recruiter_number', e.target.value)}
                      maxLength={10}
                      className={`w-full px-3 py-2 border ${borderClass} ${inputBgClass} ${inputTextClass} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.recruiter_number && <p className="text-red-500 text-sm mt-1">{errors.recruiter_number}</p>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        
        <div className={`p-6 border-t ${borderClass} flex justify-between`}>
          <button
            onClick={onClose}
            className={`px-6 py-2 border ${borderClass} rounded-md font-medium transition-colors hover:bg-gray-100 ${isDark ? 'hover:bg-gray-700' : ''} text-red-500 hover:text-red-600`}
          >
            Cancel Job Posting
          </button>
          <div className="flex gap-3">
            {!isEditing ? (
              <>
                <button
                  onClick={handleEdit}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={handleSaveEdits}
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors"
                >
                  Proceed
                </button>
              </>
            ) : (
              <button
                onClick={handleProceed}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors"
              >
                Confirm Values
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPostingConfirmationModal;
